import type { NavigationSectionGroup } from "@/components/Navigation/NavigationCard";
import { CForm } from "@/features/c-form/CForm";
import useZodValidation from "@/hooks/useZodValidation";
import { defaultNS, useTranslation } from "@/lib/i18n";
import { queryClient } from "@/lib/queryClient";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect } from "react";
import {
  useAppIntegrationContract,
  useSoftwareFromAppIntegration,
  useUpdateSoftwareDetailMutator,
} from "../-query";
import { appIntegrationKey } from "../-query/key";
import { CompatibilityEnvironmentSection } from "./-components/CompatibilityEnvironmentSection";
import { LinkSection } from "./-components/LinkSection";
import { MaintenanceSupportSection } from "./-components/MaintenanceSupportSection";
import { OperationalStatusSection } from "./-components/OperationalStatusSection";
import { ProvisioningDistributionSection } from "./-components/ProvisioningDistributionSection";
import { ResponsibilitySection } from "./-components/ResponsibilitySection";
import { SecurityComplianceSection } from "./-components/SecurityComplianceSection";
import { SoftwareIdSection } from "./-components/SoftwareIdSection";
import { VendorSection } from "./-components/VendorSection";
import {
  AppIntegrationDetailStore,
  useAppIntegrationDetailStore,
} from "./-store/store";

export const Route = createFileRoute(
  "/_staff/app-integrations/$id/_$id/detail/",
)({
  component: AppIntegrationDetail,
});

function AppIntegrationDetail() {
  const store = AppIntegrationDetailStore.createStore();
  return (
    <AppIntegrationDetailStore.Provider value={store}>
      <Content />
    </AppIntegrationDetailStore.Provider>
  );
}

function Content() {
  const { id } = Route.useParams();
  const { data } = useSoftwareFromAppIntegration(id);
  const { data: appIntegrationData } = useAppIntegrationContract(id);

  const { values, actions } = useAppIntegrationDetailStore();
  useEffect(() => {
    if (data) {
      actions.reset(data);
    }
  }, [actions.reset, data]);

  const updateSoftwareDetailMutation = useUpdateSoftwareDetailMutator(
    data?.id || id,
    {
      onSuccess: async () => {
        await queryClient.refetchQueries({
          queryKey: appIntegrationKey.software(id),
        });
      },
    },
  );

  const handleSave = useCallback(async () => {
    if (!actions.validate()) {
      throw new Error("Validation failed");
    }
    const result = await updateSoftwareDetailMutation.mutateAsync(values);
    return result;
  }, [actions.validate, updateSoftwareDetailMutation.mutateAsync, values]);

  const handleReset = useCallback(() => {
    if (data) {
      actions.reset(data);
    }
  }, [actions.reset, data]);

  useZodValidation();
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.software.profile.detail.navigation",
  });

  const navigationSectionGroups: NavigationSectionGroup[] = [
    {
      titleLabel: t("softwareIdInformation"),
      to: "",
      hash: "app-integration-id-information",
    },
    {
      titleLabel: t("link"),
      to: "",
      hash: "link",
    },
    {
      titleLabel: t("responsibility"),
      to: "",
      hash: "responsibility",
    },
    {
      titleLabel: t("vendor"),
      to: "",
      hash: "vendor",
    },
    {
      titleLabel: t("maintenanceSupport"),
      to: "",
      hash: "maintenance-support",
    },
    {
      titleLabel: t("provisioningDistribution"),
      to: "",
      hash: "provisioning-distribution",
    },
    {
      titleLabel: t("compatibilityEnvironment"),
      to: "",
      hash: "compatibility-environment",
    },
    {
      titleLabel: t("securityCompliance"),
      to: "",
      hash: "security-compliance",
    },
    {
      titleLabel: t("operationalStatus"),
      to: "",
      hash: "operational-status",
    },
  ];

  return (
    <CForm
      editUnit="section"
      itemLayout="label"
      navigationSectionGroups={navigationSectionGroups}
      onSave={handleSave}
      onReset={handleReset}
    >
      <SoftwareIdSection integratedApp={appIntegrationData.integratedApp} />
      <LinkSection />
      <ResponsibilitySection />
      <VendorSection />
      <MaintenanceSupportSection />
      <ProvisioningDistributionSection />
      <CompatibilityEnvironmentSection />
      <SecurityComplianceSection />
      <OperationalStatusSection />
    </CForm>
  );
}
